import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLang } from "@/i18n";

const links = [
  { to: "/", label: { it: "Home", en: "Home" } },
  { to: "/chi-siamo", label: { it: "Chi Siamo", en: "About" } },
  { to: "/servizi", label: { it: "Servizi", en: "Services" } },
  { to: "/prodotti", label: { it: "Prodotti", en: "Products" } },
  { to: "/prova-a-casa", label: { it: "Prova a Casa", en: "Home Trial" } },
  { to: "/partner", label: { it: "Partner", en: "Partner" } },
  { to: "/blog", label: { it: "Blog", en: "Blog" } },
  { to: "/contatti", label: { it: "Contatti", en: "Contact" } },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { lang, setLang, tr } = useLang();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const LangSwitch = ({ className = "", suffix = "" }) => (
    <div className={`flex items-center gap-1 font-mono text-[11px] tracking-widest ${className}`} data-testid="lang-switch">
      {["it", "en"].map((l) => (
        <button
          key={l}
          onClick={() => setLang(l)}
          data-testid={`lang-${l}${suffix}`}
          className={`px-2 py-1 uppercase transition-colors ${
            lang === l ? "text-[#007A8A] border border-[#007A8A]/40" : "text-[#52525b] hover:text-[#0a0a0a]"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );

  return (
    <>
      <motion.header
        data-testid="site-nav"
        initial={{ y: -60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
          scrolled ? "glass-strong" : "bg-transparent"
        }`}
      >
        <div className="max-w-[1600px] mx-auto flex items-center justify-between px-6 lg:px-12 h-20">
          <Link to="/" data-testid="nav-logo" className="flex items-center group">
            <img
              src="/brand/logo-light.png"
              alt="Aéràs Technology"
              className="h-14 w-auto"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-6">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-link-${l.to.replace(/\//g, "") || "home"}`}
                className={({ isActive }) =>
                  `aero-link font-mono text-[11px] tracking-[0.15em] uppercase transition-colors ${
                    isActive ? "text-[#007A8A]" : "text-[#3f3f46] hover:text-[#0a0a0a]"
                  }`
                }
              >
                {tr(l.label)}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <LangSwitch className="hidden sm:flex" />
            <Link
              to="/preventivo"
              data-testid="nav-cta-preventivo"
              className="hidden md:inline-flex btn-primary"
            >
              {tr({ it: "Preventivo", en: "Get a quote" })} →
            </Link>
            <button
              type="button"
              data-testid="nav-mobile-toggle"
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden p-2 border border-black/10"
              aria-label="Menu"
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-x-0 top-20 z-40 lg:hidden glass-strong border-t border-black/10"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-8 flex flex-col gap-5">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  className={({ isActive }) =>
                    `font-display text-3xl tracking-tighter ${
                      isActive ? "text-[#007A8A]" : "text-[#0a0a0a]"
                    }`
                  }
                >
                  {tr(l.label)}
                </NavLink>
              ))}
              <div className="flex items-center justify-between pt-4">
                <Link to="/preventivo" className="btn-primary w-fit">
                  {tr({ it: "Richiedi preventivo", en: "Request a quote" })}
                </Link>
                <LangSwitch suffix="-mobile" />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
